<template>
    <section
        v-if="items"
        class="container relative">
        <AtomTitle> {{ title }} </AtomTitle>

        <div
            v-if="description"
            v-html="description"
            class="mt-5 text-16/148 tinyeditor text-accent-100 md:w-2/3"></div>

        <div class="relative z-30">
            <OurAdvantagesItems
                :items
                :class="advantagesItemsClass"
                :is-changed-class="Boolean(advantagesItemsClass)"
                class="max-lg:hidden" />
            <OurAdvantagesSlider
                :items
                class="lg:hidden" />
        </div>

        <div
            v-if="bottomText"
            v-html="bottomText"
            class="mt-10 text-16/148 tinyeditor text-accent-100 md:w-2/3"></div>
    </section>
</template>

<script setup lang="ts">
import AtomTitle from '~/components/atom/atom-title/AtomTitle.vue'
import OurAdvantagesItems from '~/components/pages/directions/our-advantages-section/OurAdvantagesItems.vue'
import OurAdvantagesSlider from '~/components/pages/directions/our-advantages-section/OurAdvantagesSlider.vue'
import type { IOurAdvantagesItem } from '~/components/pages/directions/our-advantages-section/our-advantages.types'

interface TBenefitItem {
    title: string | null
    description: string | null
}

interface Props {
    title?: string
    description?: string
    bottomText?: string
    directionSlug: string
    benefits?: TBenefitItem[]
    advantagesItemsClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    title: 'Наши преимущества',
    description: '',
    bottomText: '',
    benefits: () => [],
    advantagesItemsClass: '',
})

type ItemsModule = { default: IOurAdvantagesItem[] }
type ItemsLoader = () => Promise<ItemsModule>

const fallbackItemsLoader: ItemsLoader = () =>
    import('./const/solution-culture.const')

const itemsLoader: Record<string, ItemsLoader> = {
    'radioguide-rent': () => import('./const/radioguide-rent.const'),
    'arenda-audioguides': () => import('./const/arenda-audioguides.const'),
    'arenda-konferents-system': () =>
        import('./const/arenda-konferents-system.const'),
    'arenda-oborudovanie-dlya-sinhronnogo-perevoda': () =>
        import('./const/arenda-oborudovanie-dlya-sinhronnogo-perevoda.const'),
    'arenda-radioguides': () => import('./const/arenda-radioguides.const'),
    'arenda-video': () => import('./const/arenda-video.const'),
    'arenda-zvuka': () => import('./const/arenda-zvuka.const'),
    audioguide: () => import('./const/audioguide.const'),
    cabin: () => import('./const/cabin.const'),
    'conference-system': () => import('./const/conference-system.const'),
    dostupsreda: () => import('./const/dostupsreda.const'),
    radioguide: () => import('./const/radioguide.const'),
    sinhroperevod: () => import('./const/sinhroperevod.const'),

    culture: () => import('./const/solution-culture.const'),
    sport: fallbackItemsLoader,
}

const items = await getItems()

async function getItems(): Promise<IOurAdvantagesItem[] | null> {
    const loader = itemsLoader[props.directionSlug]
    const templateItems = await (loader || fallbackItemsLoader)().then(
        (module) => module.default.map((item) => ({ ...item }))
    )

    if (props.benefits && props.benefits.length) {
        return props.benefits.map((benefit, index) => {
            const templateItem =
                templateItems[index] ||
                templateItems[index % templateItems.length]

            return {
                ...templateItem,
                title: benefit.title || templateItem.title,
                description: benefit.description || templateItem.description,
            }
        })
    }

    if (!loader) return null

    return templateItems
}
</script>
